import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService, { ListResult } from "../../services/ApiService";
import {
  CreateSchoolInput,
  SchoolData,
  UpdateSchoolInput,
} from "../../model/SchoolData";

// Upper bound for the school pickers on the classes page.
const ALL_SCHOOLS_LIMIT = 1000;

interface SchoolsState {
  schools: SchoolData[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  // Every school, for dropdowns.
  allSchools: SchoolData[];
  isLoading: boolean;
  error: string | null;
}

const initialState: SchoolsState = {
  schools: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 10,
  allSchools: [],
  isLoading: false,
  error: null,
};

export const fetchSchools = createAsyncThunk<
  ListResult<SchoolData>,
  { page: number; pageSize: number; searchKey?: string },
  { rejectValue: string }
>("schools/fetchSchools", async (params, { rejectWithValue }) => {
  const res = await apiService.getSchools({
    take: params.pageSize,
    skip: (params.page - 1) * params.pageSize,
    search: params.searchKey,
  });
  if (res.success && res.data) return res.data;
  return rejectWithValue(res.message || "Failed to fetch schools");
});

export const fetchAllSchools = createAsyncThunk<
  SchoolData[],
  void,
  { rejectValue: string }
>("schools/fetchAllSchools", async (_, { rejectWithValue }) => {
  const res = await apiService.getSchools({ take: ALL_SCHOOLS_LIMIT, skip: 0 });
  if (res.success && res.data) return res.data.data;
  return rejectWithValue(res.message || "Failed to fetch schools");
});

export const createSchool = createAsyncThunk<
  SchoolData,
  CreateSchoolInput,
  { rejectValue: string }
>("schools/createSchool", async (data, { rejectWithValue }) => {
  const res = await apiService.createSchool(data);
  if (res.success && res.data) return res.data;
  return rejectWithValue(res.message || "Failed to create school");
});

export const updateSchool = createAsyncThunk<
  SchoolData,
  { id: string; data: UpdateSchoolInput },
  { rejectValue: string }
>("schools/updateSchool", async ({ id, data }, { rejectWithValue }) => {
  const res = await apiService.updateSchool(id, data);
  if (res.success && res.data) return res.data;
  return rejectWithValue(res.message || "Failed to update school");
});

const schoolsSlice = createSlice({
  name: "schools",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchools.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSchools.fulfilled, (state, action) => {
        state.isLoading = false;
        state.schools = action.payload.data;
        state.totalCount = action.payload.total;
      })
      .addCase(fetchSchools.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to fetch schools";
      })
      .addCase(fetchAllSchools.fulfilled, (state, action) => {
        state.allSchools = action.payload;
      })
      .addCase(fetchAllSchools.rejected, (state, action) => {
        state.error = action.payload ?? "Failed to fetch schools";
      })
      .addCase(createSchool.rejected, (state, action) => {
        state.error = action.payload ?? "Failed to create school";
      })
      .addCase(updateSchool.fulfilled, (state, action) => {
        const idx = state.schools.findIndex((s) => s.id === action.payload.id);
        if (idx !== -1) state.schools[idx] = action.payload;
      })
      .addCase(updateSchool.rejected, (state, action) => {
        state.error = action.payload ?? "Failed to update school";
      });
  },
});

export const { clearError, setPage, setPageSize } = schoolsSlice.actions;

type Root = { schools: SchoolsState };
export const selectSchools = (state: Root) => state.schools.schools;
export const selectAllSchools = (state: Root) => state.schools.allSchools;
export const selectSchoolsTotal = (state: Root) => state.schools.totalCount;
export const selectSchoolsPage = (state: Root) => state.schools.currentPage;
export const selectSchoolsPageSize = (state: Root) => state.schools.pageSize;
export const selectSchoolsLoading = (state: Root) => state.schools.isLoading;
export const selectSchoolsError = (state: Root) => state.schools.error;

export default schoolsSlice.reducer;
